import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import useModal, { anyModalOpen, isInnermost } from '../ui/useModal';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import { LAYERS } from '../ui/layers';
import { useIssueReport } from '../issues/IssueReportProvider';
import { WorkbenchContext } from './WorkbenchContext';
import useDrawerAddress, { useDrawerMemory } from './drawerAddress';
import { useStageSize } from './fit';
import { placeFloat, samePlace } from './floatPlace';
import WorkbenchTooltip from './WorkbenchTooltip';

/*
 * The analysis workbench's shell (docs/ANALYSIS_WORKBENCH.md): a page exactly
 * the height of the screen that never scrolls, with
 *
 *   a top bar      fixed height: the way back, the title, the mode switcher,
 *                  the mode's context, more actions, Report an issue
 *   the stage      the rest: the picture in its fit box, and over it, never
 *                  resizing it, the overlays (stageTop, status, overlay) and
 *                  the docks the drawers come into (start, end, bottom)
 *   a toolbar      fixed height: the slots roi, tools, view, target, edit, the
 *                  drawers' toggles and save
 *   floating       the tool in hand's options (ToolOptions), over the stage's
 *   options        bottom edge beside the tool's button, lifted above an open
 *                  drawer (./floatPlace.js; section 4b)
 *   a tooltip      the name, and any description, of every control that does
 *                  not show it (./WorkbenchTooltip.js; section 2b)
 *
 * The active mode is its child and fills the places through WorkbenchSlot,
 * WorkbenchStage and WorkbenchDrawer (./WorkbenchContext.js,
 * ./WorkbenchDrawer.js). The shell keeps the open drawers in the address
 * (./drawerAddress.js), focus on its way in and out of them, and Escape.
 *
 * Props: title (required, the page's <h1>), back {to, label}, switcher (node),
 * actions (node), compact (true/false overrides the media query), testId,
 * onFiles(file) (a file dropped anywhere on the workbench: the page hands it
 * to detection; the stage is outlined while one is dragged over it),
 * editRow (2 puts the `edit` slot on the second row, between the drawers'
 * toggles and save, where a phone's first row has no room for it).
 *
 * variant="overlay" (2026-10-26, Field Mode's annotate) is the same layout
 * over another page that keeps running beneath it (the Field camera): a
 * modal dialog named by its title (an <h2>), on `layer` (default the dialog
 * layer), described by `describedBy`; focus goes in (to `initialFocusRef`,
 * else the title), Tab stays inside, focus goes back when it closes. Its
 * drawers are kept in memory, not in the address (it has none of its own: the
 * page beneath keeps its address and its Back). The top bar's x
 * (`closeLabel`, test id `<testId>-close`) and Escape with nothing else open
 * call `onClose`; `busy` (or a mode's useWorkBusy) holds it open, and holds
 * the page variant's Back too. Escape goes, in order, to a dialog
 * inside it, a popover, the drawer holding focus or else the last opened, then
 * onClose. On the canvas the canvas hears it first (it cancels a stroke) and
 * the chain goes on, as in any dialog; on the page variant's canvas it stays
 * the canvas's.
 *
 * Unsaved work (2026-10-27): a mode says it has some with useUnsavedWork(dirty)
 * or useWorkbench().setDirty(dirty), a host with the `dirty` prop. While it
 * has, the way out (the back link; the overlay's x and Escape) asks first,
 * Keep working or Discard (`discardText` overrides the words; test ids
 * `<testId>-discard`, `-keep-editing`, `-discard-confirm`), and so does
 * whatever a mode or page passes through useWorkbench().guard(proceed) (the
 * mode switcher, opening another file or capture); the browser's own close or
 * reload gets its beforeunload prompt. `guardRef` hands the page the same
 * guard, for what it renders around the shell. A save clears it: the mode's
 * dirty goes false.
 */
// Compact: the tool palette, the zoom bar and a first-row edit group (clear / undo) become popovers
// (below this, a toolbar row cannot hold them; on a 375 px phone every mode's first row overflowed).
// Their buttons show their labels there; choosing a tool closes the palette, so the tool's floating
// options show beside its button.
const COMPACT_QUERY = '(max-width: 999px)';
const SIDES = ['start', 'end', 'bottom'];

function useCompact(override) {
  const query = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(COMPACT_QUERY) : null;
  const [matches, setMatches] = useState(() => !!query?.matches);
  useEffect(() => {
    if (!query) return undefined;
    const onChange = () => setMatches(!!query.matches);
    onChange();
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, [query?.media]); // eslint-disable-line react-hooks/exhaustive-deps
  return typeof override === 'boolean' ? override : matches;
}

/** <html> and <body> locked while the workbench is on the screen: nothing behind it scrolls either. */
function useScrollLock() {
  useLayoutEffect(() => {
    const els = [document.documentElement, document.body];
    const before = els.map(el => el.style.overflow);
    els.forEach((el) => { el.style.overflow = 'hidden'; });
    return () => els.forEach((el, i) => { el.style.overflow = before[i]; });
  }, []);
}

/** Whether a drag carries files (a dragged link or text is not one). */
const carriesFiles = e => [...(e.dataTransfer?.types || [])].includes('Files');

export default function WorkbenchShell({ variant = 'page', ...props }) {
  return variant === 'overlay' ? <OverlayShell {...props} /> : <PageShell {...props} />;
}

/** A page of its own: the drawers in its address, the back link followed by the router. */
function PageShell(props) {
  const address = useDrawerAddress();
  const go = useNavigate();
  return <Shell {...props} address={address} go={go} overlay={false} />;
}

/** Over another page: the drawers in memory. */
function OverlayShell(props) {
  const memory = useDrawerMemory();
  return <Shell {...props} address={memory} overlay />;
}

/** The overlay as a modal dialog: focus in and back, Tab kept inside. Escape is the shell's own. */
function OverlayModal({ panelRef, initialFocusRef, skipRef }) {
  useModal(panelRef, { initialFocusRef, skipRef, escape: false });
  return null;
}

function Shell({ title, back = null, switcher = null, actions = null, compact: compactProp, testId = 'workbench',
                 editRow = 1, onClose = null, closeLabel = null, busy: busyProp = false, layer = LAYERS.dialog,
                 describedBy, initialFocusRef = null, onFiles = null, dirty: dirtyProp = false, discardText = null,
                 guardRef = null, address, go = null, overlay, children }) {
  const { t } = useTranslation('workbench');
  const report = useIssueReport();
  const compact = useCompact(compactProp);
  useScrollLock();
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const titleRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();
  const stageSize = useStageSize(stageRef);

  // The places the mode fills: set by callback refs, so a portal waits for its place.
  const [nodes, setNodes] = useState({});
  const refFor = useMemo(() => {
    const cache = {};
    return name => (cache[name] ||= (el) => setNodes(prev => (prev[name] === el ? prev : { ...prev, [name]: el })));
  }, []);
  const slot = useCallback(name => nodes[name] || null, [nodes]);
  const dock = useCallback(side => nodes[`dock-${side}`] || null, [nodes]);

  // ── the drawers ──────────────────────────────────────────────────────────
  const [metas, setMetas] = useState([]);
  const dismissers = useRef({});
  const openers = useRef({});
  const focusRequest = useRef(null);
  const registerDrawer = useCallback((meta) => {
    setMetas(prev => [...prev.filter(m => m.id !== meta.id), meta]);
    return () => setMetas(prev => prev.filter(m => m !== meta));
  }, []);
  const setDismiss = useCallback((id, fn) => { dismissers.current[id] = fn; }, []);
  const metaOf = useCallback(id => metas.find(m => m.id === id), [metas]);
  const isOpen = useCallback(id => address.list.includes(id) || !!metaOf(id)?.forceOpen, [address.list, metaOf]);

  // Drawer ids are plain words (drawerAddress.parseDrawers): nothing to escape in the selector.
  const toggleOf = id => rootRef.current?.querySelector(`[data-wb-toggle="${id}"]`);
  const returnFocus = useCallback((id) => {
    const opener = openers.current[id];
    delete openers.current[id];
    const target = opener && opener.isConnected && !opener.closest?.('[hidden]') ? opener : toggleOf(id);
    target?.focus?.({ preventScroll: true });
  }, []);
  // A focus request is taken by the drawer once it is shown: {id, target}, target a function
  // giving the element to focus (else its title). Never a timer: a router may commit later.
  const takeFocusRequest = useCallback((id) => {
    const request = focusRequest.current;
    if (request?.id !== id) return null;
    focusRequest.current = null;
    return request;
  }, []);

  const open = useCallback((id, { focus = true, replace = false } = {}) => {
    const target = typeof focus === 'function' ? focus : null;
    if (isOpen(id)) {
      target?.()?.focus?.({ preventScroll: true });
      return;
    }
    const active = document.activeElement;
    openers.current[id] = active && active !== document.body ? active : null;
    if (focus) focusRequest.current = { id, target };
    address.open(id, { replace });
  }, [isOpen, address]);
  const close = useCallback((id, { focus = true } = {}) => {
    const wasListed = address.close(id);
    if (!wasListed && metaOf(id)?.forceOpen) dismissers.current[id]?.();
    if (focus) returnFocus(id);
  }, [address, metaOf, returnFocus]);
  const toggle = useCallback(id => (isOpen(id) ? close(id) : open(id)), [isOpen, open, close]);
  const drawers = useMemo(() => ({ isOpen, open, close, toggle, list: address.list }),
    [isOpen, open, close, toggle, address.list]);

  // ── popovers (compact: the tool palette and the zoom bar) ────────────────
  const [popover, setPopover] = useState(null);
  useEffect(() => { if (!compact) setPopover(null); }, [compact]);
  useEffect(() => {
    if (!popover) return undefined;
    const away = (e) => {
      const root = rootRef.current;
      if (!root) return;
      const inside = e.target.closest?.(`[data-wb-popover="${popover}"], [data-wb-popover-trigger="${popover}"]`);
      if (!inside) setPopover(null);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [popover]);

  // ── the tool in hand's floating options (ToolOptions): hidden by their x or Escape until the
  //    tool's button is pressed again or the tool is put down; placed beside that button (./floatPlace.js) ──
  const [floatHidden, setFloatHidden] = useState(null);
  const [floatPlace, setFloatPlace] = useState(null);
  /** The button the options of `tool` belong to: its own, else (inside the closed palette) the palette's. */
  const anchorOf = useCallback((tool) => {
    const root = rootRef.current;
    if (!root) return null;
    const own = /^[\w-]+$/.test(tool || '') ? root.querySelector(`[data-wb-tool="${tool}"]`) : null;
    if (own && !own.closest('[hidden]')) return own;
    return root.querySelector('[data-wb-popover-trigger="tools"]') || own;
  }, []);
  const hideFloat = useCallback((tool, { focus = true } = {}) => {
    setFloatHidden(tool);
    if (focus) anchorOf(tool)?.focus?.({ preventScroll: true });
  }, [anchorOf]);
  /** The tool put down (its ToolOptions gone): taken up again by any way, its options show again. */
  const unhideFloat = useCallback((tool) => {
    setFloatHidden(hidden => (hidden === tool ? null : hidden));
  }, []);
  /** Placed again: `gone`, the tool whose options are on their way out (ToolOptions' cleanup). */
  const placeFloatNow = useCallback(({ gone = null } = {}) => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const box = nodes.float;
    const panel = box?.querySelector?.('[data-wb-float]');
    if (!root || !stage || !panel || box.hidden || panel.getAttribute('data-wb-float') === gone) {
      setFloatPlace(prev => (prev ? null : prev));
      return;
    }
    const anchor = anchorOf(panel.getAttribute('data-wb-float'));
    const blockers = [...stage.querySelectorAll('.wb-drawer')]
      .filter(d => !d.hidden).map(d => d.getBoundingClientRect());
    const next = placeFloat({
      root: root.getBoundingClientRect(), stage: stage.getBoundingClientRect(),
      anchor: anchor ? anchor.getBoundingClientRect() : null,
      size: { width: panel.offsetWidth, height: panel.offsetHeight }, blockers,
    });
    setFloatPlace(prev => (samePlace(prev, next) ? prev : next));
  }, [nodes.float, anchorOf]);
  // After every render of the shell (drawers, popovers, compact) and of the options (the mode's,
  // which render on their own: ToolOptions asks), and whenever the workbench or the toolbar changes size.
  useLayoutEffect(() => { placeFloatNow(); });
  useEffect(() => {
    if (typeof ResizeObserver !== 'function') return undefined;
    const watched = [rootRef.current, stageRef.current, nodes.float, rootRef.current?.querySelector('.wb-toolbar')];
    const observer = new ResizeObserver(() => placeFloatNow());
    watched.filter(Boolean).forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [placeFloatNow, nodes.float]);
  /** A tool's button pressed: its options show again; on a phone the palette closes, so they show beside it. */
  const onToolPressed = (e) => {
    const button = e.target?.closest?.('[data-wb-tool]');
    if (!button || !rootRef.current?.contains(button) || button.disabled) return;
    setFloatHidden(null);
    if (compact && popover === 'tools' && button.closest('[data-wb-popover="tools"]')) {
      setPopover(null);
      rootRef.current.querySelector('[data-wb-popover-trigger="tools"]')?.focus?.({ preventScroll: true });
    }
  };

  // ── the tool in hand, read off the tools slot (a radio checked or a button pressed there): the
  //    phone palette's button shows it. The mode renders on its own, so its changes are watched. ──
  const [inHand, setInHand] = useState(null);
  useEffect(() => {
    const node = nodes.tools;
    if (!compact || !node) {
      setInHand(null);
      return undefined;
    }
    const read = () => {
      const on = node.querySelector('[role="radio"][aria-checked="true"], [aria-pressed="true"]');
      const label = on?.getAttribute('aria-label') || '';
      const icon = on?.querySelector('.wb-icon')?.textContent || '';
      setInHand(prev => (on && label
        ? (prev && prev.label === label && prev.icon === icon ? prev : { label, icon })
        : null));
    };
    read();
    if (typeof MutationObserver !== 'function') return undefined;
    const observer = new MutationObserver(read);
    observer.observe(node, { subtree: true, childList: true, attributes: true,
                             attributeFilter: ['aria-checked', 'aria-pressed', 'aria-label'] });
    return () => observer.disconnect();
  }, [compact, nodes.tools]);

  // ── unsaved work: what a mode reports (useUnsavedWork, setDirty) or the host says (`dirty`) ──
  const [dirtyFrom, setDirtyFrom] = useState(() => new Set());
  const reportDirty = useCallback((source, value) => setDirtyFrom((prev) => {
    if (prev.has(source) === !!value) return prev;
    const next = new Set(prev);
    if (value) next.add(source);
    else next.delete(source);
    return next;
  }), []);
  const setDirty = useCallback(value => reportDirty('mode', value), [reportDirty]);
  // Work under way (useWorkBusy, or the host's `busy`): the way out is held until it ends.
  const [busyFrom, setBusyFrom] = useState(() => new Set());
  const reportBusy = useCallback((source, value) => setBusyFrom((prev) => {
    if (prev.has(source) === !!value) return prev;
    const next = new Set(prev);
    if (value) next.add(source);
    else next.delete(source);
    return next;
  }), []);
  const busy = !!busyProp || busyFrom.size > 0;
  const dirty = !!dirtyProp || dirtyFrom.size > 0;
  const dirtyNow = useRef(dirty);
  dirtyNow.current = dirty;
  const [asking, setAsking] = useState(null);           // {proceed}: what waits for Keep or Discard
  const keepRef = useRef(null);
  /** `proceed` at once while nothing is unsaved; else once the person chooses Discard. True when it ran now. */
  const guard = useCallback((proceed) => {
    if (!dirtyNow.current) {
      proceed();
      return true;
    }
    setAsking({ proceed });
    return false;
  }, []);
  useLayoutEffect(() => {
    if (guardRef) guardRef.current = guard;
  });
  const discard = () => {
    const proceed = asking?.proceed;
    setAsking(null);
    proceed?.();
  };
  // The browser's own close or reload: its prompt, only while there is work to lose.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ── Escape: a popover first; then the drawer holding focus, else the last one opened;
  //    over another page, then the way out ──
  const latest = useRef({});
  latest.current = { popover, drawers, metas, overlay, busy, onClose, hideFloat };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      const { popover: pop, drawers: d, metas: list, overlay: over, busy: held, onClose: leave,
              hideFloat: hideOptions } = latest.current;
      // A dialog takes it first: one opened inside (a question, a scanner) or over the workbench.
      // The overlay is itself in the dialogs' stack, so it asks whether it is the innermost one.
      if (over ? !isInnermost(rootRef.current) : anyModalOpen()) return;
      const active = document.activeElement;
      if (pop) {
        e.preventDefault();
        setPopover(null);
        rootRef.current?.querySelector(`[data-wb-popover-trigger="${pop}"]`)?.focus?.({ preventScroll: true });
        return;
      }
      // Focus in a tool's floating options: they hide, focus goes back to the tool.
      const options = active?.closest?.('[data-wb-float]');
      if (options && rootRef.current?.contains(options)) {
        e.preventDefault();
        hideOptions(options.getAttribute('data-wb-float'));
        return;
      }
      const holder = active?.closest?.('.wb-drawer');
      if (holder && rootRef.current?.contains(holder)) {
        e.preventDefault();
        d.close(holder.dataset.drawer);
        return;
      }
      // On the canvas Escape is the canvas's (it cancels a stroke). Over another page the
      // canvas hears it first and the chain goes on, as in any dialog: Escape is its way out.
      if (!over && active?.closest?.('.wb-stage-content')) return;
      const open = [...d.list].reverse().find(id => list.some(m => m.id === id))
        || list.filter(m => m.forceOpen).map(m => m.id).pop();
      if (open) {
        e.preventDefault();
        d.close(open);
        return;
      }
      if (over && leave) {
        e.preventDefault();
        if (!held) guard(leave);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [guard]);

  const context = useMemo(() => ({
    inShell: true, compact, stageSize, slot, dock, drawers, registerDrawer, setDismiss, takeFocusRequest, returnFocus,
    dirty, setDirty, reportDirty, guard, busy, reportBusy, floatHidden, hideFloat, unhideFloat,
    placeFloat: placeFloatNow,
  }), [compact, stageSize, slot, dock, drawers, registerDrawer, setDismiss, takeFocusRequest, returnFocus,
       dirty, setDirty, reportDirty, guard, busy, reportBusy, floatHidden, hideFloat, unhideFloat, placeFloatNow]);

  // ── a file dropped on the workbench: to the page (detection), the stage outlined meanwhile ──
  const [dropping, setDropping] = useState(false);
  const dropProps = onFiles ? {
    onDragOver: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setDropping(true);
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); },
    onDrop: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      setDropping(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) onFiles(file);
    },
  } : {};

  const sorted = [...metas].sort((a, b) => (a.order - b.order) || a.id.localeCompare(b.id));
  const openSides = Object.fromEntries(SIDES.map(side => [side, sorted.some(m => m.side === side && isOpen(m.id))]));

  const popoverSlot = (name, icon) => {
    if (!compact) return <div className={`wb-slot wb-slot-${name}`} data-slot={name} ref={refFor(name)} />;
    const shown = popover === name;
    // The palette's button shows the tool in hand (its icon, and in its name), so a phone tells which it is.
    const held = name === 'tools' ? inHand : null;
    return (
      <div className={`wb-slot wb-slot-${name} wb-has-popover`}>
        <button type="button" className="wb-btn" data-wb-popover-trigger={name} data-testid={`wb-popover-${name}`}
                aria-expanded={shown} aria-controls={`wb-popover-${name}-panel`}
                aria-label={held ? t('toolbar.toolsInHand', { tool: held.label }) : t(`toolbar.${name}`)}
                data-in-hand={held ? held.label : undefined}
                onClick={() => setPopover(p => (p === name ? null : name))}>
          <span aria-hidden="true" className="wb-icon">{held?.icon || icon}</span>
        </button>
        <div id={`wb-popover-${name}-panel`} className="wb-popover" data-wb-popover={name} role="group"
             aria-label={t(`toolbar.${name}`)} hidden={!shown} data-slot={name} ref={refFor(name)} />
      </div>
    );
  };

  const editSlot = <div className="wb-slot wb-slot-edit" data-slot="edit" ref={refFor('edit')} />;
  // Over another page: a modal dialog, named by its title, above the page. Its strokes and
  // pinches are its own: a host's gestures (hooks/useViewportGestures.js) skip a dialog and data-gesture="off".
  const Title = overlay ? 'h2' : 'h1';
  const dialog = overlay
    ? { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId, 'aria-describedby': describedBy || undefined,
        'data-gesture': 'off', style: { zIndex: layer } }
    : {};
  const closeText = closeLabel || t('close');
  const words = { title: t('discard.title'), body: t('discard.body'), keep: t('discard.keep'),
                  discard: t('discard.discard'), ...(discardText || {}) };

  return (
    <WorkbenchContext.Provider value={context}>
      <style>{SHELL_CSS}</style>
      <div ref={rootRef} className="wb-root" data-testid={testId} data-compact={compact ? '' : undefined}
           data-dropping={dropping ? '' : undefined} {...dropProps} onClick={onToolPressed}
           data-variant={overlay ? 'overlay' : undefined} {...dialog}
           data-float={floatPlace && !floatPlace.lifted ? '' : undefined}>
        {overlay && <OverlayModal panelRef={rootRef} initialFocusRef={initialFocusRef || titleRef} skipRef={closeRef} />}
        <header className="wb-top" data-testid="wb-top">
          {overlay && onClose && (
            <button ref={closeRef} type="button" className="wb-btn wb-close" disabled={busy}
                    onClick={() => { if (!busy) guard(onClose); }} aria-label={closeText}
                    data-testid={`${testId}-close`}>
              <span aria-hidden="true">×</span>
            </button>
          )}
          {back && (
            <Link to={back.to} className="wb-btn wb-back" aria-label={back.label} data-testid="wb-back"
                  aria-disabled={busy || undefined}
                  onClick={(e) => {
                    // Held while work is under way (a save, a decode).
                    if (busy) {
                      e.preventDefault();
                      return;
                    }
                    // Unsaved work: asked first, then followed by the router.
                    if (!dirtyNow.current || !go) return;
                    e.preventDefault();
                    guard(() => go(back.to));
                  }}>
              <svg aria-hidden="true" className="wb-chevron" viewBox="0 0 16 16" width="16" height="16">
                <path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="wb-label">{back.label}</span>
            </Link>
          )}
          <Title ref={titleRef} id={titleId} tabIndex={overlay ? -1 : undefined} className="wb-title">{title}</Title>
          {switcher}
          <div className="wb-context" data-slot="context" ref={refFor('context')} />
          <div className="wb-top-actions">
            {actions}
            {report && (
              <button type="button" className="wb-btn" onClick={report.openReport} aria-haspopup="dialog"
                      aria-label={t('report')} data-testid="wb-report">
                <span aria-hidden="true" className="wb-icon">⚑</span>
              </button>
            )}
          </div>
        </header>

        <main className="wb-stage" ref={stageRef} data-testid="wb-stage"
              style={floatPlace ? { '--wb-float-h': `${floatPlace.height}px` } : undefined}
              data-open-start={openSides.start ? '' : undefined} data-open-end={openSides.end ? '' : undefined}
              data-open-bottom={openSides.bottom ? '' : undefined}>
          <div className="wb-stage-content" data-slot="stage" ref={refFor('stage')} />
          <div className="wb-stage-top" data-slot="stageTop" ref={refFor('stageTop')} />
          <div className="wb-overlay" data-slot="overlay" ref={refFor('overlay')} />
          {SIDES.map(side => (
            <div key={side} className="wb-dock" data-side={side} data-testid={`wb-dock-${side}`} ref={refFor(`dock-${side}`)} />
          ))}
          <div className="wb-status" data-slot="status" ref={refFor('status')} />
        </main>

        <div className="wb-toolbar" role="group" aria-label={t('toolbar.label')} data-testid="wb-toolbar">
          <div className="wb-row wb-row-canvas">
            <div className="wb-slot wb-slot-roi" data-slot="roi" ref={refFor('roi')} />
            {popoverSlot('tools', '🛠️')}
            {/* The tool in hand's options: here in the order of focus (right after the tools), placed over
                the stage beside the tool (root-relative, like the popovers); hidden while a popover is open. */}
            <div className="wb-float" data-slot="float" ref={refFor('float')} data-testid="wb-float"
                 hidden={!!popover} data-placed={floatPlace ? '' : undefined}
                 style={floatPlace ? { left: floatPlace.left, top: floatPlace.top, '--wb-float-arrow': `${floatPlace.arrow}px` }
                   : undefined} />
            {popoverSlot('view', '🔍')}
            <div className="wb-slot wb-slot-target" data-slot="target" ref={refFor('target')} />
            {/* On a phone the first row has no room for clear / undo inline: a popover, as the tools and zoom. */}
            {editRow !== 2 && popoverSlot('edit', '✏️')}
          </div>
          <div className="wb-row wb-row-actions">
            <div className="wb-toggles" role="group" aria-label={t('toolbar.drawers')}>
              {sorted.map(m => (
                <button key={m.id} type="button" className="wb-btn wb-toggle" data-wb-toggle={m.id}
                        data-testid={`wb-toggle-${m.id}`} aria-expanded={isOpen(m.id)} aria-controls={`wb-drawer-${m.id}`}
                        aria-label={m.label || m.title}
                        data-tip={m.title && m.label && m.title !== m.label ? m.title : undefined}
                        onClick={() => toggle(m.id)}>
                  {m.icon && <span aria-hidden="true" className="wb-icon">{m.icon}</span>}
                  <span className="wb-label">{m.label || m.title}</span>
                </button>
              ))}
            </div>
            {editRow === 2 && editSlot}
            <div className="wb-slot wb-slot-save wb-save" data-slot="save" ref={refFor('save')} />
          </div>
        </div>
        {/* The mode: everything it shows goes through the slots above (portals); this box
            takes no place in the grid, so nothing it might render in place can resize it. */}
        <div className="wb-mode" data-testid="wb-mode">{children}</div>
        <WorkbenchTooltip rootRef={rootRef} />
        {/* Unsaved work and a way out taken: Keep working (Escape here) or Discard. */}
        {asking && (
          <Dialog title={words.title} onClose={() => setAsking(null)} layer={LAYERS.confirm} width={420}
                  initialFocusRef={keepRef} testId={`${testId}-discard`}>
            <p style={{ marginTop: 0 }}>{words.body}</p>
            <ActionBar>
              <Button ref={keepRef} onClick={() => setAsking(null)} data-testid={`${testId}-keep-editing`}>
                {words.keep}
              </Button>
              <Button variant="danger" onClick={discard} data-testid={`${testId}-discard-confirm`}>{words.discard}</Button>
            </ActionBar>
          </Dialog>
        )}
      </div>
    </WorkbenchContext.Provider>
  );
}

/*
 * The layout's guarantees live here (section 1): the root is the screen's
 * height and hides its overflow; the top bar and the toolbar have fixed
 * heights; every dock, overlay and popover is an absolute child of the stage
 * or of the root, so none of them can change the stage's size; and the only
 * boxes that scroll are drawer bodies. Both variants keep clear of a phone's
 * notch and rounded corners (safe-area insets, as the Field camera does).
 */
const SHELL_CSS = `
.wb-root { --wb-top-h: 52px; --wb-row-h: 44px; --wb-tool-h: calc(2 * var(--wb-row-h) + 12px);
  position: fixed; inset: 0; top: var(--view-as-offset, 0px); height: calc(100dvh - var(--view-as-offset, 0px));
  display: grid; grid-template-rows: calc(var(--wb-top-h) + env(safe-area-inset-top, 0px)) minmax(0, 1fr)
    calc(var(--wb-tool-h) + env(safe-area-inset-bottom, 0px));
  grid-template-columns: minmax(0, 1fr); overflow: hidden; background: var(--c-bg-subtle); color: var(--c-text-fg);
  font-size: 14px; }
.wb-top { display: flex; align-items: center; gap: 8px; padding: 0 12px; min-width: 0; overflow: hidden;
  background: var(--c-primary); color: var(--c-on-accent); white-space: nowrap; }
.wb-top .wb-btn { color: var(--c-on-accent); background: rgba(255,255,255,0.14); border-color: transparent; }
.wb-top .wb-btn[aria-checked="true"], .wb-top .wb-btn[aria-expanded="true"] { background: var(--c-on-accent);
  color: var(--c-primary); border-color: transparent; }
.wb-top .wb-btn:focus-visible { outline-color: var(--c-on-accent); }
.wb-title { margin: 0; font-size: 16px; font-weight: 700; flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.wb-context { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; display: flex; gap: 6px; align-items: center; }
.wb-top-actions { display: flex; gap: 6px; align-items: center; flex: none; }
.wb-back { text-decoration: none; }
.wb-back[aria-disabled="true"] { opacity: .5; cursor: progress; }
[dir="rtl"] .wb-chevron { transform: scaleX(-1); }
.wb-stage { position: relative; overflow: hidden; min-height: 0; min-width: 0; background: #1b1d1f; }
.wb-root[data-dropping] .wb-stage { outline: 3px dashed var(--c-primary); outline-offset: -6px; }
.wb-stage-content { position: absolute; inset: 0; overflow: hidden; }
.wb-stage-top { position: absolute; top: 8px; inset-inline: 8px; z-index: 10; display: flex; flex-direction: column;
  gap: 6px; align-items: stretch; pointer-events: none; }
.wb-stage-top > * { pointer-events: auto; }
.wb-overlay { position: absolute; inset: 0; z-index: 35; pointer-events: none; overflow: hidden; }
.wb-overlay:empty { display: none; }
.wb-overlay > * { pointer-events: auto; }
.wb-status { position: absolute; bottom: 8px; inset-inline: 8px; z-index: 40; display: flex; flex-direction: column;
  align-items: center; gap: 4px; pointer-events: none; }
.wb-root[data-float] .wb-status { bottom: calc(16px + var(--wb-float-h, 48px)); }
.wb-status > * { pointer-events: auto; max-width: min(640px, 100%); box-shadow: 0 2px 10px rgba(0,0,0,0.25);
  background: var(--c-surface); border-radius: 8px; padding: 6px 12px; }
.wb-stage[data-open-bottom] .wb-status { bottom: calc(40% + 16px); }
.wb-dock { position: absolute; z-index: 30; display: flex; flex-direction: column; gap: 8px; pointer-events: none;
  min-height: 0; }
.wb-dock[data-side="start"] { top: 8px; bottom: 8px; inset-inline-start: 8px; width: min(360px, calc(100% - 16px)); }
.wb-dock[data-side="end"] { top: 8px; bottom: 8px; inset-inline-end: 8px; width: min(360px, calc(100% - 16px)); }
.wb-dock[data-side="bottom"] { bottom: 8px; inset-inline: 8px; max-height: 40%; }
.wb-stage[data-open-bottom] .wb-dock[data-side="start"], .wb-stage[data-open-bottom] .wb-dock[data-side="end"] {
  bottom: calc(40% + 16px); }
.wb-drawer { pointer-events: auto; flex-direction: column; min-height: 0; max-height: 100%; flex: 0 1 auto;
  background: var(--c-surface); color: var(--c-text-fg); border: 1px solid var(--c-border); border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.3); overflow: hidden; }
.wb-drawer-head { display: flex; align-items: center; gap: 8px; padding: 8px 8px 8px 12px; flex: none;
  border-bottom: 1px solid var(--c-border); }
.wb-drawer-title { margin: 0; font-size: 15px; flex: 1; min-width: 0; outline: none; }
.wb-drawer-body { overflow-y: auto; overscroll-behavior: contain; min-height: 0; flex: 1 1 auto; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 10px; }
.wb-close { font-size: 20px; line-height: 1; }
.wb-fit { overflow: hidden; }
.wb-empty { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1;
  width: max-content; max-width: min(520px, calc(100% - 32px)); display: flex; flex-direction: column; gap: 10px;
  align-items: flex-start; padding: 16px; border-radius: 12px; background: var(--c-surface); color: var(--c-text-fg);
  box-shadow: 0 8px 28px rgba(0,0,0,0.3); }
.wb-switcher { display: flex; gap: 4px; align-items: center; flex: none; }
.wb-chip { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 13px; }
.wb-overlay-body { position: absolute; inset: 8px; overflow-y: auto; overscroll-behavior: contain; padding: 10px;
  background: var(--c-surface); color: var(--c-text-fg); border-radius: 12px; box-shadow: 0 8px 28px rgba(0,0,0,0.3); }
.wb-switcher select { height: 36px; border-radius: 8px; font: inherit; }
.wb-mode { position: absolute; width: 0; height: 0; overflow: hidden; }
.wb-toolbar { display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 6px 8px;
  padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px)); overflow: hidden; min-width: 0;
  background: var(--c-surface); border-top: 1px solid var(--c-border); }
.wb-row { display: flex; align-items: center; gap: 6px; height: var(--wb-row-h); min-width: 0; overflow: hidden;
  flex-wrap: nowrap; }
.wb-row-actions { justify-content: space-between; }
.wb-slot { display: flex; align-items: center; gap: 4px; flex: none; min-width: 0; }
.wb-slot:empty { display: none; }
.wb-slot + .wb-slot:not(:empty) { padding-inline-start: 6px; border-inline-start: 1px solid var(--c-border); }
.wb-slot-save { margin-inline-start: auto; }
.wb-toggles + .wb-slot-edit:not(:empty) { padding-inline-start: 6px; border-inline-start: 1px solid var(--c-border); }
.wb-root .wb-top { padding-top: env(safe-area-inset-top, 0px);
  padding-left: max(12px, env(safe-area-inset-left, 0px)); padding-right: max(12px, env(safe-area-inset-right, 0px)); }
.wb-root .wb-toolbar { padding-left: max(8px, env(safe-area-inset-left, 0px));
  padding-right: max(8px, env(safe-area-inset-right, 0px)); }
.wb-group { display: flex; gap: 4px; align-items: center; flex: none; }
/* A group in a popover is held to its width, so its labelled buttons wrap (unheld, a row of seven ran 718 px). */
.wb-popover .wb-group { flex-wrap: wrap; flex: 1 1 100%; min-width: 0; max-width: 100%; }
.wb-toggles { display: flex; gap: 4px; align-items: center; min-width: 0; }
.wb-has-popover { position: static; }
.wb-popover { position: absolute; bottom: calc(var(--wb-tool-h) + env(safe-area-inset-bottom, 0px) + 6px);
  inset-inline: 8px; z-index: 50; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 8px;
  background: var(--c-surface); border: 1px solid var(--c-border); border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.3); }
.wb-popover[hidden] { display: none; }
/* On a phone the popovers have the room: their buttons say what they are. */
.wb-root .wb-popover .wb-label { display: inline; }
.wb-float { position: absolute; z-index: 45; left: 8px; bottom: calc(var(--wb-tool-h) + env(safe-area-inset-bottom, 0px) + 8px);
  max-width: calc(100% - 16px); }
.wb-float[data-placed] { bottom: auto; }
.wb-float:empty, .wb-float[hidden] { display: none; }
.wb-float-panel { position: relative; display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px;
  max-width: min(560px, calc(100vw - 16px)); padding: 6px 8px; background: var(--c-surface); color: var(--c-text-fg);
  border: 1px solid var(--c-primary); border-radius: 12px; box-shadow: 0 6px 22px rgba(0,0,0,0.35); font-size: 13px; }
.wb-float-panel::after { content: ''; position: absolute; bottom: -7px; left: calc(var(--wb-float-arrow, 50%) - 7px);
  width: 12px; height: 12px; background: var(--c-surface); border-right: 1px solid var(--c-primary);
  border-bottom: 1px solid var(--c-primary); transform: rotate(45deg); }
.wb-float-title { display: inline-flex; align-items: center; gap: 4px; font-weight: 700; white-space: nowrap; }
.wb-float-field { display: inline-flex; align-items: center; gap: 6px; min-width: 0; white-space: nowrap; }
.wb-float-field input[type="range"] { width: clamp(110px, 32vw, 200px); height: 32px; accent-color: var(--c-primary); }
.wb-float-field input[type="checkbox"] { width: 20px; height: 20px; }
.wb-float-value { min-width: 2.5em; font-variant-numeric: tabular-nums; font-weight: 600; }
.wb-float-note { color: var(--c-text-muted-fg); white-space: nowrap; }
.wb-tip { position: fixed; z-index: 70; max-width: min(320px, calc(100vw - 12px)); padding: 6px 10px;
  border-radius: 8px; background: var(--c-text-fg); color: var(--c-surface); font-size: 13px; line-height: 1.35;
  box-shadow: 0 4px 16px rgba(0,0,0,0.3); display: flex; flex-direction: column; gap: 2px; pointer-events: auto;
  white-space: normal; }
.wb-tip-name { font-weight: 700; }
.wb-tip-desc { font-weight: 400; }
.wb-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-width: 40px; height: 40px;
  padding: 0 10px; border-radius: 8px; border: 1px solid var(--c-border-strong); background: var(--c-surface);
  color: var(--c-text-fg); font: inherit; font-size: 13px; cursor: pointer; white-space: nowrap; flex: none;
  -webkit-touch-callout: none; user-select: none; }
.wb-btn:disabled { opacity: 0.45; cursor: default; }
.wb-btn:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 2px; }
.wb-btn[aria-checked="true"], .wb-btn[aria-pressed="true"], .wb-btn[aria-expanded="true"] {
  background: var(--c-primary-soft); border-color: var(--c-primary); font-weight: 700; }
.wb-btn.wb-primary { background: var(--c-primary); color: var(--c-on-accent); border-color: var(--c-primary); font-weight: 600; }
.wb-btn.wb-primary:disabled { background: var(--c-text-faint); border-color: transparent; }
.wb-icon { font-size: 16px; line-height: 1; }
.wb-toolbar .wb-label { display: none; }
.wb-toolbar .wb-save .wb-label { display: inline; }
.wb-root[data-compact] .wb-toolbar .wb-save .wb-label { display: none; }
@media (min-width: 1280px) {
  .wb-toolbar .wb-toggle .wb-label, .wb-toolbar .wb-slot-target .wb-label { display: inline; }
}
@media (max-width: 720px) {
  .wb-top { padding: 0 6px; gap: 6px; }
  .wb-root .wb-top { padding-left: max(6px, env(safe-area-inset-left, 0px));
    padding-right: max(6px, env(safe-area-inset-right, 0px)); }
  .wb-top .wb-label { display: none; }
  /* The page's name stays its heading for a screen reader; the mode switcher says the rest. */
  .wb-title { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  .wb-dock[data-side="start"] { width: calc(100% - 16px); }
  .wb-dock[data-side="end"], .wb-dock[data-side="bottom"] { top: auto; bottom: 8px; inset-inline: 8px; width: auto;
    max-height: 60%; }
  .wb-stage[data-open-end][data-open-bottom] .wb-dock[data-side="end"] { bottom: calc(30% + 12px); max-height: 30%; }
  .wb-stage[data-open-end][data-open-bottom] .wb-dock[data-side="bottom"] { max-height: 30%; }
  .wb-stage[data-open-bottom] .wb-dock[data-side="start"] { bottom: 8px; }
  .wb-stage[data-open-end] .wb-status, .wb-stage[data-open-bottom] .wb-status { bottom: calc(60% + 16px); }
  .wb-btn { min-width: 40px; padding: 0 8px; }
}
`;
